import { Ruleset } from 'entities/campaign';
import { put, select, take, takeEvery } from 'redux-saga/effects';
import { Actions as RouterActions, Selectors as RouterSelectors } from 'router';
import { Actions as CampaignActions, Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { GenericAction as RestAPIAction, Types as RestAPITypes } from 'services/genericAPIGatewayService';
import { Actions as RulesetActions, Selectors as RulesetSelectors } from '../rulesets';
import { Service } from '../services';
import { Selectors as EditorSelectors } from './selectors';
import { SaveChanges, Types } from './types';
import { Actions as EditorActions } from './actions';

export function* saveChanges(action: SaveChanges) {
  const campaignId = yield select(RouterSelectors.getCampaignId);
  const newRulesets: { [tempId: string]: Ruleset } = yield select(RulesetSelectors.getNewEntityMap);
  const newRulesetIds = Object.keys(newRulesets).filter(x => newRulesets[x].campaignId === campaignId);

  for (const newRulesetId of newRulesetIds) {
    yield put(RulesetActions.requestCreate(newRulesetId, newRulesets[newRulesetId]));
  }

  const successes: { [tempId: string]: string } = {};

  // Save new rulesets
  // eslint-disable-next-line
  for (const _ of newRulesetIds) {
    const result: RestAPIAction<any, any> = yield take(
      (action: RestAPIAction<any, any>) =>
        (action.type === RestAPITypes.CREATE_COMPLETED || action.type === RestAPITypes.CREATE_FAILED) &&
        action.service === Service.Rulesets,
    );

    if (result.type === RestAPITypes.CREATE_COMPLETED) {
      successes[result.payload.tempId] = result.payload.id;
    }
  }

  const allSavesSuccessful = Object.keys(successes).length === newRulesetIds.length;
  if (!allSavesSuccessful) {
    return;
  }

  const updatedCampaign = yield select(CampaignSelectors.getUpdatedEntity, campaignId);
  const rulesetIdsWithTemporaryIds = yield select(EditorSelectors.getRulesetIds);
  const rulesetIds = rulesetIdsWithTemporaryIds.map((x: string) => successes[x] || x);

  // save campaign with new ruleset order

  yield put(
    CampaignActions.requestUpdate(campaignId, {
      ...updatedCampaign,
      rulesetPriorities: rulesetIds,
    }),
  );

  // save updated rulesets

  for (const rulesetId of rulesetIds) {
    const hasChanges = yield select(RulesetSelectors.hasChanges, rulesetId);
    if (hasChanges) {
      const newRuleset = yield select(RulesetSelectors.getUpdatedEntity, rulesetId);
      yield put(RulesetActions.requestUpdate(rulesetId, newRuleset));
    }
  }

  // delete any rulesets that are marked for delete

  const rulesetsToDelete = yield select(EditorSelectors.getDeletedRulesetIds);
  for (const rulesetId of rulesetsToDelete) {
    yield put(RulesetActions.requestDelete(rulesetId));
    yield put(EditorActions.clearRuleset());
  }

  // Redirect back to campaigns list if we're provided with a siteId
  if (action?.payload?.siteId) {
    yield put(RouterActions.push(`/sites/${action.payload.siteId}/campaigns/campaigns`));
  }
}

export function* listenForSave() {
  yield takeEvery(Types.SAVE_CHANGES, saveChanges);
}
